import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Card, Col, DatePicker, Radio, Row, Space, Statistic, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '@/lib/api';
import { formatRupiah } from '@/lib/format';
export function SalesSummaryPage() {
    const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]);
    const [groupBy, setGroupBy] = useState('day');
    const q = useQuery({
        queryKey: [
            'sales-summary',
            range[0].format('YYYY-MM-DD'),
            range[1].format('YYYY-MM-DD'),
            groupBy,
        ],
        queryFn: async () => (await api.get('/reports/sales-summary', {
            params: {
                from: range[0].format('YYYY-MM-DD'),
                to: range[1].format('YYYY-MM-DD'),
                group_by: groupBy,
            },
        })).data,
    });
    const cols = [
        { title: 'Kelompok', dataIndex: 'bucket', width: 200 },
        {
            title: 'Bruto',
            dataIndex: 'gross',
            align: 'right',
            render: (v) => formatRupiah(v),
        },
        {
            title: 'HPP',
            dataIndex: 'hpp',
            align: 'right',
            render: (v) => formatRupiah(v),
        },
        {
            title: 'Margin',
            dataIndex: 'margin',
            align: 'right',
            render: (v) => (_jsx("b", { style: { color: parseFloat(v) >= 0 ? '#059669' : '#DC2626' }, children: formatRupiah(v) })),
        },
        { title: 'Transaksi', dataIndex: 'tx_count', align: 'right', width: 120 },
    ];
    const chartData = (q.data?.rows ?? []).map((r) => ({
        bucket: r.bucket,
        gross: parseFloat(r.gross),
        margin: parseFloat(r.margin),
    }));
    return (_jsxs("div", { children: [_jsxs(Space, { style: { marginBottom: 16, display: 'flex', justifyContent: 'space-between' }, wrap: true, children: [_jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Ringkasan Penjualan" }), _jsxs(Space, { children: [_jsx(Radio.Group, { value: groupBy, onChange: (e) => setGroupBy(e.target.value), optionType: "button", options: [
                                    { label: 'Harian', value: 'day' },
                                    { label: 'Bulanan', value: 'month' },
                                    { label: 'Kategori', value: 'category' },
                                ] }), _jsx(DatePicker.RangePicker, { value: range, onChange: (v) => v && v[0] && v[1] && setRange([v[0], v[1]]) })] })] }), _jsxs(Row, { gutter: 16, style: { marginBottom: 16 }, children: [_jsx(Col, { span: 6, children: _jsx(Card, { children: _jsx(Statistic, { title: "Bruto", value: parseFloat(q.data?.total_gross ?? '0'), formatter: (v) => formatRupiah(Number(v)), valueStyle: { color: '#0F766E' } }) }) }), _jsx(Col, { span: 6, children: _jsx(Card, { children: _jsx(Statistic, { title: "HPP", value: parseFloat(q.data?.total_hpp ?? '0'), formatter: (v) => formatRupiah(Number(v)), valueStyle: { color: '#B91C1C' } }) }) }), _jsx(Col, { span: 6, children: _jsx(Card, { children: _jsx(Statistic, { title: "Margin", value: parseFloat(q.data?.total_margin ?? '0'), formatter: (v) => formatRupiah(Number(v)), valueStyle: {
                                    color: parseFloat(q.data?.total_margin ?? '0') >= 0 ? '#059669' : '#DC2626',
                                } }) }) }), _jsx(Col, { span: 6, children: _jsx(Card, { children: _jsx(Statistic, { title: "Jumlah Transaksi", value: q.data?.total_tx ?? 0 }) }) })] }), _jsx(Card, { size: "small", style: { marginBottom: 16 }, children: _jsx("div", { style: { height: 280 }, children: _jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(BarChart, { data: chartData, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: "#EEE" }), _jsx(XAxis, { dataKey: "bucket" }), _jsx(YAxis, { tickFormatter: (v) => `Rp ${(v / 1_000_000).toFixed(0)}jt` }), _jsx(Tooltip, { formatter: (v) => formatRupiah(v) }), _jsx(Bar, { dataKey: "gross", fill: "#B91C1C", name: "Bruto" }), _jsx(Bar, { dataKey: "margin", fill: "#D97706", name: "Margin" })] }) }) }) }), _jsx(Card, { children: _jsx(Table, { rowKey: "bucket", size: "middle", pagination: false, loading: q.isLoading, columns: cols, dataSource: q.data?.rows ?? [] }) })] }));
}
